import { useState, useEffect, useRef, useId } from "react";

interface QuoteLinkItem {
  product_id: string;
  variant_id?: string;
  quantity: number;
}

interface QuoteLinkContact {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}

interface QuoteLinkDiscount {
  description: string;
  amountCents: number;
}

interface QuoteLinkModalProps {
  schoolId?: string;
  schoolName?: string;
  items: QuoteLinkItem[];
  contact?: QuoteLinkContact;
  adminFirstName?: string;
  adminLastName?: string;
  adminEmail?: string;
  paymentMethod?: "card" | "invoice";
  discounts?: QuoteLinkDiscount[];
  onClose: () => void;
}

const SALES_PASSWORD_KEY = "smartered-sales-password";

export function QuoteLinkModal({ schoolId, schoolName, items, contact, adminFirstName, adminLastName, adminEmail, paymentMethod, discounts, onClose }: QuoteLinkModalProps) {
  const [step, setStep] = useState<"generating" | "success" | "error">("generating");
  const [quoteUrl, setQuoteUrl] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  // Keep the latest onClose in a ref so the dialog keyboard effect below can
  // run exactly once per mount even if the parent passes an inline callback.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Dialog accessibility (WAI-ARIA modal dialog pattern) — see
  // docs/deep-analysis-2026-07-05.md: modal previously had no Escape handling,
  // no focus management, and no focus trap. The component is only mounted
  // while the modal is open, so mount == open and unmount == close.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeBtnRef.current?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !modalRef.current) return;

      // Simple focus trap: cycle Tab / Shift+Tab within the modal content.
      const focusables = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute("disabled"));
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      const inside = active instanceof Node && modalRef.current.contains(active);

      if (e.shiftKey) {
        if (!inside || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (!inside || active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      // Return focus to whatever opened the modal.
      previouslyFocused?.focus();
    };
  }, []);

  // Generate link on mount
  useEffect(() => {
    generateLink();
  }, []);

  async function generateLink() {
    const password = sessionStorage.getItem(SALES_PASSWORD_KEY);

    if (!schoolId || !schoolName) {
      setError("School information is required");
      setStep("error");
      return;
    }

    if (!password) {
      setError("Not authenticated. Please login via /salesteam first.");
      setStep("error");
      return;
    }

    try {
      const url = `/api/orders/quote-link?_t=${Date.now()}`;
      const body: Record<string, unknown> = {
        school_id: schoolId,
        school_name: schoolName,
        items: items.map(item => ({
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
        })),
        sales_password: password,
        expires_days: 30,
      };

      // Include contact if provided (phone is optional)
      if (contact && contact.firstName && contact.lastName && contact.email) {
        body.contact = {
          first_name: contact.firstName,
          last_name: contact.lastName,
          email: contact.email,
          phone: contact.phone || "",
        };
      }

      // Include admin details if provided
      if (adminEmail) {
        if (adminFirstName) body.admin_first_name = adminFirstName;
        if (adminLastName) body.admin_last_name = adminLastName;
        body.admin_email = adminEmail;
      }

      // Include payment method if provided
      if (paymentMethod) {
        body.payment_method = paymentMethod;
      }

      // Include discounts if provided
      if (discounts && discounts.length > 0) {
        body.discounts = discounts.filter(d => d.amountCents > 0).map(d => ({
          description: d.description,
          amountCents: d.amountCents,
        }));
      }

      const response = await fetch(url, {
        method: "POST",
        cache: "no-store",
        credentials: "omit",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        if (response.status === 401) {
          sessionStorage.removeItem(SALES_PASSWORD_KEY);
          setError("Invalid password. Please login via /salesteam again.");
          setStep("error");
          return;
        }
        throw new Error(data.message || "Failed to generate quote link");
      }

      const data = await response.json();
      setQuoteUrl(data.url);
      setExpiresAt(new Date(data.expires_at).toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }));
      setStep("success");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setStep("error");
    }
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(quoteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = quoteUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <button ref={closeBtnRef} className="modal-close" onClick={onClose} aria-label="Close">
          &times;
        </button>

        {step === "generating" && (
          <div className="modal-loading">
            <div className="loading-spinner" />
            {/* Only one step renders at a time, so the three step headings share titleId */}
            <p id={titleId}>Generating quote link...</p>
          </div>
        )}

        {step === "success" && (
          <>
            <h2 className="modal-title" id={titleId}>Quote Link Ready</h2>
            <p className="modal-description">
              Send this link to the customer. The link pre-populates the order form with the selected products for <strong>{schoolName}</strong>.
            </p>
            <div className="quote-link-container">
              <input
                type="text"
                value={quoteUrl}
                readOnly
                className="quote-link-input"
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <button
                type="button"
                className={`btn ${copied ? "btn-added" : "btn-primary"}`}
                onClick={handleCopy}
              >
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
            <p className="quote-link-expiry">
              Link expires: {expiresAt}
            </p>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary btn-full" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        )}

        {step === "error" && (
          <>
            <h2 className="modal-title" id={titleId}>Error</h2>
            <p className="modal-description modal-error">{error}</p>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setStep("generating");
                  generateLink();
                }}
              >
                Try Again
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
